import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconOffice3Outline18 as Building2,
	IconChevronRightOutline18 as ChevronRight,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import { cn } from "@productlane/lib/src/cn"
import { isCustomContactEmail } from "@productlane/lib/src/custom-channel"
import { IconButton, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { RoleCheck } from "@/components/shared/role-check"
import { isEmailBlocked } from "@/lib/email-utils"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { ContactSelect } from "@/screens/app/contacts/contact-select"
import {
	collectThreadParticipantEmails,
	rankContactSuggestionEmails,
} from "@/screens/app/contacts/thread-contact-suggestions"
import { CustomersSelect } from "@/screens/app/customers/customers-select"
import { selectorActionClassName } from "@/screens/app/selector-action-button"
import { PainLevelSelect } from "@/screens/app/threads/pain-level-select"
import { SlaBadge } from "@/screens/app/threads/sla-badge"
import { useThreadContext } from "../../thread-context"
import { AgentResolutionBadge } from "./agent-resolution-badge"
import { AssigneeSelect } from "./assignee-select"
import { ContactSuppressionWarning } from "./contact-suppression-warning"
import { SidebarGroup, SidebarGroupLabel } from "./shared/sidebar-group"
import { ThreadPortalRow } from "./thread-portal-row"
import { ThreadSlackChannelRow } from "./thread-slack-channel-row"

export function SidebarTopSection() {
	const { thread } = useThreadContext()
	const zero = useZero()
	const navigate = useNavigate()
	const [teamMembers] = useQuery(queries.teamMembers())
	const [blockedEmails] = useQuery(queries.blockedSenders())
	const [slackUsers] = useQuery(queries.slackUsers())
	const workspace = useZeroWorkspace()
	const [assigneeOpen, onAssigneeOpenChange] = useToggle(false)

	const hasCompany = !!thread.company?.id
	const isBlocked = useMemo(
		() => isEmailBlocked(thread.contact?.email, blockedEmails),
		[thread.contact?.email, blockedEmails],
	)

	// Zero hands back a fresh object on every sync tick, so the pickers below
	// would re-render on each one if they took the relation directly.
	const contact = thread.contact
	const selectedContact = useMemo(
		() =>
			contact
				? {
						id: contact.id,
						email: contact.email,
						name: contact.name,
						imageUrl: contact.imageUrl,
					}
				: null,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contact?.id, contact?.email, contact?.name, contact?.imageUrl],
	)

	// Serialized so the memoized picker only re-renders when the ranking changes.
	const suggestionKey = useMemo(
		() =>
			JSON.stringify(
				rankContactSuggestionEmails({
					participantEmails: collectThreadParticipantEmails(thread, slackUsers),
					teamEmails: teamMembers.flatMap((m) => m.user?.email ?? []),
					excludedEmails: [
						...(contact?.email ? [contact.email] : []),
						...(workspace?.emailSettings ?? []).flatMap((a) => [
							a.primaryEmail,
							a.brandedInboundEmailAddress,
						]),
					].filter((email): email is string => !!email),
				}),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			thread.emails,
			thread.slackMessages,
			thread.liveChatSession,
			slackUsers,
			teamMembers,
			contact?.email,
			workspace?.emailSettings,
		],
	)
	const suggestions = useMemo(
		() =>
			JSON.parse(suggestionKey) as ReturnType<
				typeof rankContactSuggestionEmails
			>,
		[suggestionKey],
	)

	const company = thread.company
	const brandfetchData = company?.companyMeta?.brandfetchData
	const selectedCompany = useMemo(
		() =>
			company
				? {
						id: company.id,
						name: company.name,
						domains: company.domains,
						logoUrl: company.logoUrl,
						companyMeta: brandfetchData ? { brandfetchData } : null,
					}
				: null,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			company?.id,
			company?.name,
			company?.domains,
			company?.logoUrl,
			brandfetchData,
		],
	)

	return (
		<SidebarGroup>
			<SidebarGroupLabel>Details</SidebarGroupLabel>
			<div className="flex flex-col items-start gap-2">
				<div className="group/contact flex min-w-0 flex-row items-center gap-0.5">
					<ContactSelect
						displayEmail
						showCompanies
						modal={false}
						isBlocked={isBlocked}
						className="!min-w-[145px] max-w-72"
						contactId={thread.contactId}
						contact={selectedContact}
						companyId={thread.company?.id ?? null}
						suggestedEmails={suggestions.customers}
						suggestedTeamEmails={suggestions.teammates}
						onChange={async (contact) => {
							await mutateOk(
								zero.mutate(
									mutators.threads.update({
										id: thread.id,
										contactId: contact ? contact.id : null,
										companyId: contact?.companyId ?? null,
									}),
								),
							)
						}}
						onCompanySelect={async (company) => {
							await mutateOk(
								zero.mutate(
									mutators.threads.update({
										id: thread.id,
										companyId: company.id,
									}),
								),
							)
						}}
					/>
					{!hasCompany && (
						<RoleCheck not={["VIEWER"]}>
							<CustomersSelect
								showContacts
								modal={false}
								className="!min-w-0 opacity-0 group-hover/contact:opacity-100"
								trigger={<IconButton Icon={Building2} variant="ghost" />}
								companyId={thread?.company?.id ?? null}
								company={selectedCompany}
								onChange={async (company) => {
									await mutateOk(
										zero.mutate(
											mutators.threads.update({
												id: thread.id,
												companyId: company ? company.id : null,
											}),
										),
									)
								}}
								onContactSelect={async (contact) => {
									await mutateOk(
										zero.mutate(
											mutators.threads.update({
												id: thread.id,
												contactId: contact ? contact.id : null,
												companyId: contact?.companyId ?? null,
											}),
										),
									)
								}}
							/>
						</RoleCheck>
					)}
				</div>

				{thread.contactId &&
					thread.contact?.isEmailSuppressed &&
					!isCustomContactEmail(thread.contact.email) && (
						<ContactSuppressionWarning contactId={thread.contactId} />
					)}

				{hasCompany && (
					<div className="shrink-0 [&_button]:!flex-initial">
						<CustomersSelect
							showContacts
							modal={false}
							withoutIcon={hasCompany}
							className="!min-w-0"
							companyId={thread?.company?.id ?? null}
							company={selectedCompany}
							onChange={async (company) => {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											companyId: company ? company.id : null,
										}),
									),
								)
							}}
							onContactSelect={async (contact) => {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											contactId: contact ? contact.id : null,
											companyId: contact?.companyId ?? null,
										}),
									),
								)
							}}
						/>
					</div>
				)}
				<ThreadSlackChannelRow />
				<ThreadPortalRow />
				<PainLevelSelect
					modal={false}
					className="ml-px hover:bg-gray-150 dark:hover:bg-gray-800"
					painLevel={thread.painLevel}
					onChange={async (level) => {
						await mutateOk(
							zero.mutate(
								mutators.threads.update({
									id: thread.id,
									painLevel: level,
								}),
							),
						)
					}}
				/>
				<SlaBadge thread={thread} modal={false} />
				<AgentResolutionBadge thread={thread} />
				<div className="group/assignee flex min-w-0 items-center">
					<AssigneeSelect
						modal={false}
						className="hover:bg-gray-150 dark:hover:bg-gray-800"
						open={assigneeOpen}
						onOpenChange={onAssigneeOpenChange}
						assigneeId={thread.assigneeId}
						onChange={async (assigneeId) => {
							await mutateOk(
								zero.mutate(
									mutators.threads.update({
										id: thread.id,
										assigneeId,
									}),
								),
							)
						}}
						thread={thread}
						teamMembers={teamMembers}
					/>
					{thread.assigneeId && (
						<Tooltip label="Open member details">
							<IconButton
								Icon={ChevronRight}
								variant="ghost"
								size="md"
								aria-label="Open member details"
								className={cn(
									"mx-0.5 flex-none opacity-0 group-hover/assignee:opacity-100",
									selectorActionClassName,
								)}
								iconClassName="h-3.5 w-3.5"
								onClick={() => {
									if (!thread.assigneeId) return
									void navigate({
										to: "/members/$id",
										params: { id: thread.assigneeId },
									})
								}}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		</SidebarGroup>
	)
}
